import React from 'react';
import { MapPin, ExternalLink } from 'lucide-react';

interface StoreCardProps {
  id: string;
  name: string;
  category: string;
  rating: number;
  reviews: number;
  location: string;
  image: string;
  verified: boolean;
  description: string;
  featured: string[];
  website: string;
  onClick: () => void;
}

export default function StoreCard({
  id,
  name,
  category,
  rating,
  reviews,
  location,
  image,
  verified,
  description,
  featured,
  website,
  onClick,
}: StoreCardProps) {
  const [imageError, setImageError] = React.useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const imageUrl = imageError 
    ? `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(name)}`
    : image;

  return (
    <div 
      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
      onClick={onClick}
    >
      <div className="relative h-48">
        <img
          src={imageUrl}
          alt={name}
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
        {verified && (
          <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
            Verified
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-semibold text-gray-900">
            {name}
          </h3>
        </div>
        <p className="text-blue-600 font-medium mb-2">{category}</p>
        <div className="flex items-center text-gray-500 text-sm mb-4">
          <MapPin className="w-4 h-4 mr-1" />
          {location}
        </div>
        <p className="text-gray-600 mb-4 line-clamp-2">{description}</p>
        
        {/* Featured Products Preview */}
        <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
          {featured?.map((item, index) => (
            <div 
              key={index}
              className="w-16 h-16 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0 text-gray-500 text-xs text-center p-1"
            >
              {item}
            </div>
          ))}
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{reviews} reviews</span>
          {website && (
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
              onClick={(e) => e.stopPropagation()} // Prevent card click when clicking website link
            >
              Visit Website
              <ExternalLink className="w-4 h-4 ml-1" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}